<template>
  <div class="company-selector dropdown d-inline-block" style="position: relative;">
    <button class="btn btn-sm header-company-chip dropdown-toggle d-flex align-items-center gap-2"
            type="button"
            @click.prevent="toggleOpen">
      <i class="bi bi-building chip-icon"></i>
      <span class="chip-text">
        <span class="chip-label">Cliente:</span>
        <span class="chip-value">{{ empresaAtualNome || 'Minha Empresa' }}</span>
        <span class="chip-sep">·</span>
        <span class="chip-label">Organização:</span>
        <span class="chip-value">{{ organizacaoAtualNome || '—' }}</span>
      </span>
    </button>

    <!-- Nível 1: Cliente -->
    <ul v-if="isOpen && nivel === 'cliente'"
        class="dropdown-menu dropdown-menu-dark dropdown-menu-end shadow-lg show"
        style="min-width: 300px; position: absolute; inset: 0px 0px auto auto; transform: translate(0px, 34px);">
      <li class="dropdown-header-nivel">Cliente</li>
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#"
           @click.prevent="selecionarCliente(null)">
          <i class="bi bi-house-door" style="font-size: 1rem;"></i>
          <span>-- Minha Empresa --</span>
        </a>
      </li>
      <li><hr class="dropdown-divider"></li>
      <li v-for="empresa in listaEmpresas" :key="'cli-' + empresa.idEmpresa">
        <a class="dropdown-item d-flex align-items-center gap-2" href="#"
           :class="{ active: empresaAtualId === empresa.idEmpresa }"
           @click.prevent="selecionarCliente(empresa.idEmpresa)">
          <i class="bi bi-building" style="font-size: 1rem;"></i>
          <span class="text-truncate">{{ empresa.nomeEmpresa }}</span>
          <i class="bi bi-chevron-right ms-auto" style="font-size: 0.7rem; opacity: 0.7;"></i>
        </a>
      </li>
    </ul>

    <!-- Nível 2: Organização (drilldown) -->
    <ul v-if="isOpen && nivel === 'organizacao'"
        class="dropdown-menu dropdown-menu-dark dropdown-menu-end shadow-lg show"
        style="min-width: 300px; position: absolute; inset: 0px 0px auto auto; transform: translate(0px, 34px);">
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#"
           @click.prevent="voltarParaClientes">
          <i class="bi bi-chevron-left" style="font-size: 0.9rem;"></i>
          <span>Voltar</span>
        </a>
      </li>
      <li><hr class="dropdown-divider"></li>
      <li class="dropdown-header-nivel">
        Organização<span class="nivel-sub"> — {{ empresaAtualNome || 'Minha Empresa' }}</span>
      </li>
      <li v-if="carregandoOrganizacoes" class="px-3 py-2 text-center">
        <span class="spinner-border spinner-border-sm" role="status"></span>
      </li>
      <li v-else-if="listaOrganizacoes.length === 0" class="px-3 py-2 small nivel-sub">
        Nenhuma organização disponível.
      </li>
      <li v-for="org in listaOrganizacoes" v-show="!carregandoOrganizacoes" :key="'org-' + org.idOrganizacao">
        <a class="dropdown-item d-flex align-items-center gap-2" href="#"
           :class="{ active: organizacaoAtualId === org.idOrganizacao }"
           @click.prevent="selecionarOrganizacao(org.idOrganizacao)">
          <i class="bi bi-diagram-3" style="font-size: 1rem;"></i>
          <span class="text-truncate">{{ org.nomeFantasia || org.razaoSocial }}</span>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'CompanySelector',
  props: {
    listaEmpresas: { type: Array, default: () => [] },
    empresaAtualId: { type: Number, default: null },
    listaOrganizacoes: { type: Array, default: () => [] },
    organizacaoAtualId: { type: Number, default: null },
    carregandoOrganizacoes: { type: Boolean, default: false }
  },
  emits: ['selecionar-empresa', 'selecionar-organizacao'],
  data () {
    return {
      isOpen: false,
      nivel: 'cliente'
    }
  },
  computed: {
    empresaAtualNome () {
      if (!this.empresaAtualId) return null
      const found = this.listaEmpresas.find(e => e.idEmpresa === this.empresaAtualId)
      return found ? found.nomeEmpresa : null
    },
    organizacaoAtualNome () {
      if (!this.organizacaoAtualId) return null
      const found = this.listaOrganizacoes.find(o => o.idOrganizacao === this.organizacaoAtualId)
      return found ? (found.nomeFantasia || found.razaoSocial) : null
    }
  },
  methods: {
    toggleOpen () {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.nivel = 'cliente'
      }
    },
    selecionarCliente (id) {
      // Drilldown: seleciona o cliente e avança para a lista de organizações
      this.$emit('selecionar-empresa', id)
      this.nivel = 'organizacao'
    },
    voltarParaClientes () {
      this.nivel = 'cliente'
    },
    selecionarOrganizacao (id) {
      this.isOpen = false
      this.nivel = 'cliente'
      this.$emit('selecionar-organizacao', id)
    }
  },
  mounted () {
    // Fecha o dropdown quando o clique ocorrer FORA do componente.
    // Usa 'mousedown' (e NÃO 'click'): ao clicar num item, o componente re-renderiza
    // (remove o nível anterior) e o nó clicado é destacado do DOM — então o listener
    // de 'click' veria `this.$el.contains(target) === false` e fecharia o dropdown
    // indevidamente, impedindo o drilldown. O 'mousedown' dispara antes do 'click',
    // quando o alvo ainda está na árvore.
    const close = (e) => {
      if (!this.$el.contains(e.target)) {
        this.isOpen = false
      }
    }
    document.addEventListener('mousedown', close)
    this._closeHandler = close
  },
  beforeUnmount () {
    document.removeEventListener('mousedown', this._closeHandler)
  }
}
</script>

<style scoped>
.company-selector {
  white-space: nowrap;
}
/* Chip com affordance: borda, fundo sutil e hover — sobre o header de cor fixa */
.header-company-chip {
  max-width: 460px;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #E6EAEE;
  box-shadow: none;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}
.header-company-chip:hover,
.header-company-chip:focus-visible {
  background-color: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.32);
  color: #FFFFFF;
}
.header-company-chip .chip-icon {
  color: #C7D0DA;
  font-size: 0.9rem;
}
.header-company-chip:hover .chip-icon {
  color: #FFFFFF;
}
.chip-text {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
  overflow: hidden;
}
.chip-label {
  color: #C7D0DA;
  font-size: 0.78rem;
}
.header-company-chip:hover .chip-label {
  color: #E6EAEE;
}
.chip-value {
  color: #FFFFFF;
  font-weight: 600;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chip-sep {
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0.1rem;
}
.dropdown-toggle::after {
  font-size: 0.65rem;
  vertical-align: middle;
}
/* Rótulos do dropdown: cores fixas e legíveis sobre a superfície escura do menu */
.dropdown-header-nivel {
  padding: 0.25rem 1rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #C7D0DA;
}
.nivel-sub {
  color: #9AA6B2;
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}
.dropdown-item {
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  border-radius: 0.25rem;
  margin: 0.1rem 0.3rem;
  transition: background-color 0.15s ease;
  cursor: pointer;
}
.dropdown-item:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.dropdown-item.active {
  background-color: rgba(13, 110, 253, 0.2) !important;
  color: #fff !important;
}
</style>
